// src/modules/app-views/services/pages/Document/tabs/ParametersTab/modals/PreChecklistModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Flex, Space, Form, Input } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PreChecklistModal = ({ open, onCancel, onSave, initialData, loading }) => {
  // ---------------------------------------------------------------------------
  // 1. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  // ---------------------------------------------------------------------------
  // 2. LOCAL VARIABLES
  // ---------------------------------------------------------------------------
  const isEditing = !!initialData;
  const title = isEditing ? translateX('edit_item') : translateX('add_item');

  // ---------------------------------------------------------------------------
  // 3. EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (open) {
      if (initialData) {
        form.setFieldsValue({
          item: initialData.item || '',
          detail: initialData.detail || '',
        });
      } else {
        form.resetFields();
      }

      // auto-focus on item field
      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    }
  }, [open, initialData, form]);

  // ---------------------------------------------------------------------------
  // 4. HANDLERS
  // ---------------------------------------------------------------------------
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      onSave({
        item: (values.item || '').trim(),
        detail: (values.detail || '').trim(),
      });
    } catch (error) {
      // validation failed - form will show errors
    }
  }, [form, onSave]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  const handleAfterClose = useCallback(() => {
    form.resetFields();
  }, [form]);

  // ---------------------------------------------------------------------------
  // 5. UI
  // ---------------------------------------------------------------------------
  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      afterClose={handleAfterClose}
      destroyOnHidden
      centered
      getContainer={document.body}
      width={500}
      footer={
        <Flex justify="end" style={{ paddingTop: 8 }}>
          <Space>
            <Button onClick={handleCancel} disabled={loading} icon={<CloseOutlined />}>
              {translateX('cancel')}
            </Button>
            <Button
              type="primary"
              loading={loading}
              icon={<CheckOutlined />}
              onClick={handleSubmit}
            >
              {translateX('save')}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ item: '', detail: '' }}
      >
        <Form.Item
          name="item"
          label={translateX('checklist_item')}
          rules={[{ required: true, message: translateX('required_field') }]}
        >
          <Input
            ref={inputRef}
            maxLength={200}
            placeholder={translateX('checklist_item')}
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          name="detail"
          label={translateX('checklist_detail')}
        >
          <Input.TextArea
            rows={2}
            maxLength={500}
            showCount
            placeholder={translateX('checklist_detail')}
            disabled={loading}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PreChecklistModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
